import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Modal,
  Typography,
} from '@mui/material';
import { useEntityActions } from 'api/entity';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useNotifications } from 'components/notify';
import { useClipboard } from 'providers/ClipboardProvider';
import React, { useState } from 'react';
import { FinsightEntity } from 'shared';
import { useSWRConfig } from 'swr';

interface BatchDeleteEntitiesModalProps {
  open: boolean;
  onClose: () => void;
  entities: FinsightEntity[];
  onConfirm?: () => void;
}

const BatchDeleteEntitiesModal: React.FC<BatchDeleteEntitiesModalProps> = ({
  open,
  onClose,
  entities,
  onConfirm,
}) => {
  const { clearClipboard } = useClipboard();
  const { batchDeleteEntities } = useEntityActions();
  const { showNotification } = useNotifications();
  const workspaceId = useWorkspaceId();
  const { mutate, cache } = useSWRConfig();

  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = async () => {
    if (entities.length === 0) {
      return;
    }

    setIsDeleting(true);
    showNotification('Deleting entities...', { loading: true });

    try {
      const response = await batchDeleteEntities(
        entities.map((entity) => entity.id),
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete entities');
      }

      Array.from(cache.keys())
        .filter(
          (x) =>
            x.includes(`/workspace/${workspaceId}/entity`) ||
            x.includes(`/workspace/${workspaceId}/entities`),
        )
        .forEach((key) => mutate(key));

      clearClipboard();

      showNotification(`Successfully deleted ${entities.length} entities`, {
        variant: 'success',
      });

      onConfirm?.();
      handleClose();
    } catch (error) {
      console.error('Error deleting entities:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to delete entities. Please try again.';
      showNotification(errorMessage, {
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        sx={{
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: 600,
        }}
      >
        <CardHeader
          title={`Delete ${entities.length} entities`}
          action={
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          }
        />
        <CardContent
          sx={{
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
          }}
        >
          {entities.length === 0 ? (
            <Typography color="text.secondary">
              No entities selected to delete.
            </Typography>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Are you sure you want to delete the following entities? This
                action cannot be undone.
              </Typography>
              <List dense>
                {entities.map((entity) => (
                  <ListItem key={entity.id} sx={{ px: 0 }}>
                    <ListItemText
                      primary={entity.name}
                      secondary={entity.types[0]}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
        <CardActions
          sx={{
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <Button onClick={handleClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirm}
            disabled={entities.length === 0 || isDeleting}
          >
            {isDeleting ? 'Deleting...' : `Delete (${entities.length})`}
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

export default BatchDeleteEntitiesModal;
